import React, { useEffect, useState, useRef, lazy } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
const SERVICE_IMAGES = ['https://images.unsplash.com/photo-1631815588090-d4bfec5b1ccb?w=800&q=80', 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=800&q=80', 'https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=800&q=80', 'https://images.unsplash.com/photo-1551190822-a9ce113ac100?w=800&q=80', 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&q=80'];
export function ServicesCarousel() {
  const {
    t
  } = useLanguage();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const services = [{
    title: t.services.items.hospitalization.title,
    desc: t.services.items.hospitalization.desc,
    image: SERVICE_IMAGES[0]
  }, {
    title: t.services.items.preop.title,
    desc: t.services.items.preop.desc,
    image: SERVICE_IMAGES[1]
  }, {
    title: t.services.items.postop.title,
    desc: t.services.items.postop.desc,
    image: SERVICE_IMAGES[2]
  }, {
    title: t.services.items.or.title,
    desc: t.services.items.or.desc,
    image: SERVICE_IMAGES[3]
  }, {
    title: t.services.items.clinical.title,
    desc: t.services.items.clinical.desc,
    image: SERVICE_IMAGES[4]
  }];
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const idx = Number(entry.target.getAttribute('data-index'));
          if (!isNaN(idx)) setActiveIndex(idx);
        }
      });
    }, {
      root: container,
      threshold: 0.6
    });
    const cards = container.querySelectorAll('.service-card');
    cards.forEach(card => observer.observe(card));
    return () => observer.disconnect();
  }, [services]);
  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const card = scrollRef.current.querySelector('.service-card') as HTMLElement;
    if (!card) return;
    const gap = 24;
    const scrollAmount = card.offsetWidth + gap;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };
  const scrollToIndex = (index: number) => {
    if (!scrollRef.current) return;
    const cards = scrollRef.current.querySelectorAll('.service-card');
    const card = cards[index] as HTMLElement;
    if (card) {
      card.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  };
  return <div className="relative group" data-id="element-204">
      {/* Scroll Container */}
      <div ref={scrollRef} className="flex overflow-x-auto snap-x snap-mandatory gap-6 pb-4 -mx-4 px-4 lg:mx-0 lg:px-0" style={{
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }} data-id="element-205">
        <style data-id="element-206">{`div::-webkit-scrollbar { display: none; }`}</style>

        {services.map((service, index) => <div key={index} data-index={index} className="service-card flex-shrink-0 w-[85vw] sm:w-[70vw] md:w-[45vw] lg:w-[calc(33.333%-16px)] snap-center" data-id="element-207">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden h-full flex flex-col" data-id="element-208">
              {/* Image */}
              <div className="aspect-[16/10] relative overflow-hidden" data-id="element-209">
                <img src={service.image} alt={service.title} className="w-full h-full object-cover" loading="lazy" data-id="element-210" />
                {/* Subtle gradient at bottom for visual polish */}
                <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/20 to-transparent" data-id="element-211" />
                {/* Step indicator */}
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-caces-navy text-xs font-bold px-3 py-1.5 rounded-full shadow-sm" data-id="element-212">
                  0{index + 1}
                </div>
              </div>

              {/* Content */}
              <div className="p-6 flex flex-col flex-grow" data-id="element-213">
                <h3 className="font-serif text-xl font-bold text-caces-navy mb-2" data-id="element-214">
                  {service.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed flex-grow" data-id="element-215">
                  {service.desc}
                </p>
              </div>
            </div>
          </div>)}

        {/* Right spacer for mobile */}
        <div className="w-4 flex-shrink-0 lg:hidden" data-id="element-216" />
      </div>

      {/* Navigation Arrows */}
      <button onClick={() => scroll('left')} disabled={activeIndex === 0} className="hidden lg:flex absolute -left-5 xl:-left-12 top-[35%] -translate-y-1/2 w-11 h-11 bg-white rounded-full shadow-lg items-center justify-center text-caces-navy hover:text-caces-accent transition-all z-10 opacity-0 group-hover:opacity-100 disabled:opacity-0 border border-gray-100" aria-label="Previous service" data-id="element-217">
        <ChevronLeft size={22} data-id="element-218" />
      </button>
      <button onClick={() => scroll('right')} disabled={activeIndex === services.length - 1} className="hidden lg:flex absolute -right-5 xl:-right-12 top-[35%] -translate-y-1/2 w-11 h-11 bg-white rounded-full shadow-lg items-center justify-center text-caces-navy hover:text-caces-accent transition-all z-10 opacity-0 group-hover:opacity-100 disabled:opacity-0 border border-gray-100" aria-label="Next service" data-id="element-219">
        <ChevronRight size={22} data-id="element-220" />
      </button>

      {/* Progress Dots */}
      <div className="flex justify-center gap-2.5 mt-8" data-id="element-221">
        {services.map((_, index) => <button key={index} onClick={() => scrollToIndex(index)} className={`h-2 rounded-full transition-all duration-300 ${index === activeIndex ? 'w-7 bg-caces-accent' : 'w-2 bg-gray-300 hover:bg-gray-400'}`} aria-label={`Go to service ${index + 1}`} aria-current={index === activeIndex} data-id="element-222" />)}
      </div>
    </div>;
}